const PLACEHOLDER_PATTERN = /\{(@?[A-Z0-9_#]+)\}/gi;
const BRACKET_REFERENCE_PATTERN = /\[(@?[A-Z0-9_#]+)\]/gi;
const CSV_REFERENCE_PATTERN = /CSV:([^\s"'`,|<>{}\[\]]+)/gi;
const DEFAULT_COINCIDENCE = '[\\w\\s\\d¤*/@¥,.-]+';

const DATATYPE_MAP = {
  ANY: 'text',
  ALPHA: 'text',
  CHAR: 'text',
  DATE: 'date_sabre',
  DECIMAL: 'decimal',
  INT: 'numeric',
  INTEGER: 'numeric',
  NUM: 'numeric',
  NUMBER: 'numeric',
  NUMERIC: 'numeric',
  TIME: 'hour_sabre',
};

function compactObject(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => {
      if (entryValue === undefined || entryValue === null) {
        return false;
      }

      if (Array.isArray(entryValue) && entryValue.length === 0) {
        return false;
      }

      return true;
    })
  );
}

function normalizeVariableName(variableName = '') {
  return variableName.replace(/#[A-Z]$/i, '');
}

function normalizeVariableKey(name = '') {
  return String(name || '')
    .replace(/^\[|\]$/g, '')
    .replace(/#.*$/g, '')
    .trim()
    .toUpperCase();
}

function normalizeDomName(name = '') {
  return normalizeVariableName(name).replace(/^@/, '');
}

function normalizeLookupName(name = '') {
  return normalizeVariableKey(String(name || '').replace(/^@/, ''));
}

function hasWindowVariableBinding(field = {}) {
  if (!field || field.visible === false) {
    return false;
  }

  const hasBinding = Boolean(
    field.variable ||
      field.binding ||
      (field.name && (field.templateValue || field.textTemplate))
  );

  if (!hasBinding) {
    return false;
  }

  if (field.type === 'button') {
    return false;
  }

  if (field.type === 'text' && !field.variable && !field.binding && !field.templateValue && !field.textTemplate) {
    return false;
  }

  return true;
}

function inferTypedataFromContext(field = {}, variable = null) {
  const current = String(field.typedata || field.typeData || '').toLowerCase();
  const datatype = String(field.datatype || variable?.datatype || '').toLowerCase();
  const contextText = [
    field.name,
    field.binding,
    field.variable,
    field.variableBase,
    field.label,
    field.text,
    field.caption,
    variable?.name,
    variable?.label,
    datatype,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (
    [
      'select',
      'select_menu',
      'button',
      'button_menu',
      'hidden',
      'check',
      'checkbox',
      'check_group',
      'textarea',
    ].includes(current)
  ) {
    return current === 'checkbox' ? 'check' : current;
  }

  if (!hasWindowVariableBinding(field) && current === 'label') {
    return 'label';
  }

  if (datatype === 'time') {
    return 'hour_sabre';
  }

  if (/\b(hora|horario|hhmm|time)\b|deptime|returnetime|departuretime|arrivaltime/.test(contextText)) {
    return 'hour_sabre';
  }

  if (/\b(fecha|date|ddmmm)\b|pkupdate|pkupapto|pickupdate|returndate|departuredate|arrivaldate/.test(contextText)) {
    return 'date_sabre';
  }

  if (/\b(tarifa|fare|importe|monto|amount|precio|price|total|subtotal)\b/.test(contextText)) {
    return 'decimal';
  }

  if (/\b(correo|email|e-mail|mail)\b/.test(contextText)) {
    return 'mail';
  }

  if (/\b(tarjeta|card|creditcard|cc)\b/.test(contextText)) {
    return 'text';
  }

  if (/\b(numero|número|num|cantidad|count|qty)\b/.test(contextText) || datatype === 'number' || datatype === 'numeric') {
    return 'numeric';
  }

  return current || null;
}

function normalizeTypedata(field = {}, variable = null) {
  if (
    !hasWindowVariableBinding(field) &&
    (field.type === 'display' || field.controlType === 'label' || field.control === 'display')
  ) {
    return 'label';
  }

  if (field.type === 'button') {
    return 'button';
  }

  if (field.type === 'select' || field.controlType === 'select' || field.control === 'select' || field.suffix === '#D') {
    return 'select';
  }

  if (field.type === 'checkbox-group') {
    return 'check_group';
  }

  if (field.controlType === 'checkbox' || field.control === 'checkbox' || field.suffix === '#C') {
    return 'check';
  }

  const datatype = (field.datatype || variable?.datatype || '').toUpperCase();
  return DATATYPE_MAP[datatype] || inferTypedataFromContext(field, variable) || 'text';
}

function resolveFieldRequired(field = {}, variable = null) {
  if (field.required !== undefined) {
    return Boolean(field.required);
  }

  if (field.mandatory !== undefined) {
    return Boolean(field.mandatory);
  }

  return Boolean(variable?.required);
}

function resolveFieldMaxlength(field = {}, variable = null) {
  return field.maxlength ?? field.length ?? variable?.length;
}

function findVariable(ir, variableName) {
  if (!variableName) {
    return null;
  }

  const normalizedName = normalizeVariableKey(variableName);

  return (
    Object.values(ir.variables || {}).find((variable) => {
      return normalizeVariableKey(variable.name) === normalizedName;
    }) || null
  );
}

function findDefineMetadata(ir = {}, variableName = '') {
  const normalizedName = normalizeVariableKey(variableName);

  if (!normalizedName) {
    return null;
  }

  const defineNode = (ir.lineClassifications || [])
    .filter((line) => line?.type === 'define')
    .flatMap((line) => (line.definitions?.length ? line.definitions.map((definition) => ({ ...definition, line: line.line })) : [line]))
    .find((definition) => [definition.target, definition.name, definition.variable, definition.sourceVariable]
      .some((candidate) => normalizeVariableKey(candidate) === normalizedName));

  if (!defineNode) {
    return null;
  }

  return {
    name: defineNode.target || defineNode.name || variableName,
    sourceVariable: defineNode.sourceVariable,
    suffix: defineNode.suffix,
    datatype: defineNode.datatype,
    length: defineNode.length,
    required: defineNode.required === true,
    mandatory: defineNode.required === true,
    optional: defineNode.required === false,
    validationSource: 'define',
    line: defineNode.line,
    raw: defineNode.raw,
  };
}

function findVariableMetadata(ir, variableName) {
  const variable = findVariable(ir, variableName);
  const defineMetadata = findDefineMetadata(ir, variableName);

  if (!variable && !defineMetadata) {
    return null;
  }

  return {
    ...(variable || {}),
    defineMetadata,
    datatype: variable?.datatype ?? defineMetadata?.datatype,
    length: variable?.length ?? variable?.maxlength ?? defineMetadata?.length,
    required:
      variable?.required === true || defineMetadata?.required === true
        ? true
        : variable?.required ?? defineMetadata?.required,
    mandatory:
      variable?.mandatory === true || defineMetadata?.mandatory === true
        ? true
        : variable?.mandatory ?? defineMetadata?.mandatory,
    optional: variable?.optional ?? defineMetadata?.optional,
    validationSource:
      variable?.required === true || defineMetadata?.required === true
        ? variable?.validationSource || defineMetadata?.validationSource || 'define'
        : variable?.validationSource,
  };
}

function applyDefineMetadataToField(field = {}, variableMeta = null) {
  if (!field || !variableMeta) {
    return field;
  }

  const datatype = String(variableMeta.datatype || '').toUpperCase();
  const length = Number(variableMeta.length ?? variableMeta.maxlength ?? 0);
  const status = String(variableMeta.status || variableMeta.requiredType || '').toUpperCase();
  const required =
    variableMeta.required === true ||
    variableMeta.mandatory === true ||
    status === 'MAND';
  const optional = variableMeta.optional === true || status === 'OPT';

  const nextField = {
    ...field,
  };

  if (datatype) {
    nextField.datatype = datatype;
  }

  const visualTypes = ['select', 'select_menu', 'button', 'button_menu', 'check', 'check_group', 'hidden'];
  if (!nextField.displayOnly && !visualTypes.includes(nextField.typedata)) {
    const declaredType = DATATYPE_MAP[datatype];
    if (declaredType) {
      nextField.typedata = declaredType;
      if (datatype === 'DATE') {
        nextField.dataFormat = 'date_sabre';
        if (length === 5) {
          nextField.type = 'date_sabre';
        }
      }
    }
  }

  if (length > 0) {
    nextField.maxlength = length;
  }

  if (variableMeta.required !== undefined || variableMeta.mandatory !== undefined) {
    nextField.required = required;
    nextField.mandatory = required;
  } else if (optional) {
    nextField.required = false;
    nextField.mandatory = false;
  }

  return nextField;
}

function parseDefineRawMetadata(raw = '') {
  const match = String(raw || '').match(/^DEFINE\s*\[([^=\]]+)=([^\]]*)\]/i);

  if (!match) {
    return null;
  }

  const name = match[1]?.trim();
  const parts = String(match[2] || '').split(':');
  const datatype = parts[0]?.trim() || null;
  const length = /^\d+$/.test(parts[1] || '') ? Number(parts[1]) : null;
  const status = parts[2]?.trim().toUpperCase() || '';

  return {
    name,
    datatype,
    length,
    status,
    required: status === 'MAND',
    mandatory: status === 'MAND',
    optional: status === 'OPT',
    raw,
  };
}

function normalizeDefineMetadata(candidate = {}) {
  const rawMetadata = parseDefineRawMetadata(candidate.raw);
  const name = candidate.name || candidate.target || candidate.variable || candidate.sourceVariable || rawMetadata?.name;
  const candidateType = String(candidate.type || '').toUpperCase();
  const candidateDatatype = candidateType && candidateType !== 'DEFINE' && candidateType !== 'VARIABLE'
    ? candidate.type
    : null;
  const datatype = candidate.datatype || rawMetadata?.datatype || candidateDatatype;
  const length = candidate.length ?? candidate.maxlength ?? rawMetadata?.length;
  const status = String(candidate.status || candidate.requiredType || rawMetadata?.status || '').toUpperCase();
  const required =
    candidate.required === true ||
    candidate.mandatory === true ||
    status === 'MAND' ||
    rawMetadata?.required === true;
  const optional =
    candidate.optional === true ||
    status === 'OPT' ||
    rawMetadata?.optional === true;

  if (!name || !datatype) {
    return null;
  }

  return {
    name,
    datatype,
    length,
    status,
    required,
    mandatory: required,
    optional,
    line: candidate.line || rawMetadata?.line,
    raw: candidate.raw || rawMetadata?.raw,
  };
}

function addDefineMetadataCandidate(defineMap, candidate) {
  const metadata = normalizeDefineMetadata(candidate);
  const key = normalizeVariableKey(metadata?.name);

  if (!metadata || !key) {
    return;
  }

  const existing = defineMap.get(key) || {};
  const nextDatatype = metadata.datatype || existing.datatype;
  defineMap.set(key, {
    ...existing,
    ...metadata,
    required: existing.required === true || metadata.required === true,
    mandatory: existing.mandatory === true || metadata.mandatory === true,
    optional: existing.optional === true || metadata.optional === true,
    length: existing.length ?? metadata.length,
    datatype: nextDatatype,
  });
}

function buildDefineMetadataMap(ir = {}) {
  const defineMap = new Map();

  Object.values(ir.variables || {}).forEach((variable) => {
    addDefineMetadataCandidate(defineMap, variable);
  });

  Object.values(ir.defines || {}).forEach((definition) => {
    addDefineMetadataCandidate(defineMap, definition);
  });

  Object.values(ir.variableDefinitions || {}).forEach((definition) => {
    addDefineMetadataCandidate(defineMap, definition);
  });

  (ir.lineClassifications || []).forEach((line) => {
    if (line?.type === 'define' || /^DEFINE\s*\[/i.test(line?.raw || '')) {
      const definitions = line.definitions?.length ? line.definitions : [line];
      definitions.forEach((definition) => addDefineMetadataCandidate(defineMap, {
        ...definition,
        line: line.line,
      }));
    }
  });

  return defineMap;
}

function getFieldDefineKeys(field = {}) {
  return [
    field.name,
    field.binding,
    field.id,
    field.campoForm,
    field.tag,
    field.variable,
    field.variableBase,
    field._sourceName,
  ]
    .map(normalizeVariableKey)
    .filter(Boolean);
}

function applyDefineMetadataToAllFields(dataJson, ir, diagnostics = []) {
  const defineMap = buildDefineMetadataMap(ir);

  if (defineMap.size === 0) {
    return dataJson;
  }

  (dataJson.forms || []).forEach((form) => {
    (form.fields || []).forEach((field, index) => {
      if (!field || field.visible === false) {
        return;
      }

      const metadata = getFieldDefineKeys(field).map((key) => defineMap.get(key)).find(Boolean);

      if (!metadata) {
        return;
      }

      const nextField = compactObject(applyDefineMetadataToField(field, metadata));

      form.fields[index] = nextField;

      diagnostics.push({
        level: 'info',
        code: 'DEFINE_METADATA_APPLIED',
        field: nextField.name || nextField.binding,
        datatype: metadata.datatype,
        length: metadata.length,
        typedata: nextField.typedata,
        required: nextField.required,
        sourceLine: metadata.line,
      });
    });
  });

  return dataJson;
}

function findDefault(ir, variableName) {
  const normalizedName = normalizeVariableKey(variableName);

  return (ir.defaults || []).find((defaultNode) => {
    return normalizeVariableKey(defaultNode.target || '') === normalizedName;
  });
}

function getInitialValue(ir, variableName) {
  const defaultNode = findDefault(ir, variableName);

  if (!defaultNode) {
    return '';
  }

  if (defaultNode.defaultType === 'default_value' || defaultNode.defaultType === 'default_checkbox') {
    return defaultNode.value ?? '';
  }

  return '';
}

function getFieldCsvReference(field = {}, ir = {}, variableName = '') {
  const defaultNode = variableName ? findDefault(ir, variableName) : null;
  const variable = variableName ? findVariable(ir, variableName) : null;
  const fieldOptions = getFieldOptions(field);
  const optionCsvValue = fieldOptions
    .map((option) => option?.value || option?.label || option?.text || option?.source || option?.path)
    .find((value) => extractCsvReferences(value).length > 0);

  return findFirstCsvReference(
    field.value,
    field.defaultValue,
    field.initialValue,
    field.catalogSource?.source,
    field.catalogSource?.path ? `CSV:${field.catalogSource.path}` : null,
    field.catalog,
    field.datasource,
    optionCsvValue,
    defaultNode?.value,
    defaultNode?.raw,
    variable?.value,
    variable?.defaultValue,
    variable?.raw
  );
}

function attachCsvCatalogSource(field = {}, csvContext, reference, metadata = {}) {
  const dependency = addCsvDependency(csvContext, reference, metadata);

  if (!dependency) {
    return field;
  }

  addDiagnostic(csvContext.diagnostics, {
    level: 'warning',
    code: 'CSV_FIELD_WITHOUT_LOADED_CATALOG',
    field: metadata.usedBy,
    dependencyId: dependency.id,
    path: dependency.path,
    message: 'El campo depende de un CSV externo que no fue cargado como catalogo en data.json.',
  });

  const hasCsvValue = extractCsvReferences(field.value).length > 0;

  return compactObject({
    ...field,
    value: hasCsvValue ? '' : field.value,
    catalogSource: {
      type: 'csv',
      dependencyId: dependency.id,
      path: dependency.path,
    },
  });
}

function getFieldOptions(field = {}) {
  return field.options || field.datasource || [];
}

function normalizeDatasource(options = [], context = null, scopeId = '') {
  const normalizedOptions = options.map((option) => ({
    text: option.text || option.label || option.value,
    value: option.value,
  }));

  return dedupeDatasourceOptions(normalizedOptions, context, scopeId);
}

function createDuplicateReport() {
  return {
    formsSkipped: 0,
    fieldsSkipped: 0,
    optionsSkipped: 0,
    actionsSkipped: 0,
    commandsSkipped: 0,
  };
}

function addDiagnostic(diagnostics, diagnostic) {
  diagnostics.push(diagnostic);
}

function normalizeCsvPath(path = '') {
  return String(path || '').trim().replace(/\//g, '\\');
}

function normalizeCsvPathKey(path = '') {
  return normalizeCsvPath(path).toUpperCase();
}

function getCsvBasename(path = '') {
  const normalized = normalizeCsvPath(path);
  const parts = normalized.split(/[\\/]/).filter(Boolean);
  return parts[parts.length - 1] || normalized || 'csv';
}

function hashText(value = '') {
  let hash = 0;

  for (const char of String(value)) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }

  return hash.toString(36);
}

function createCsvDependencyId(path = '', usedIds = new Set()) {
  const baseName = getCsvBasename(path).replace(/\.csv$/i, '');
  const slug = slugify(baseName) || 'dependency';
  const baseId = `csv_${slug}`;

  if (!usedIds.has(baseId)) {
    usedIds.add(baseId);
    return baseId;
  }

  const withHash = `${baseId}_${hashText(normalizeCsvPathKey(path)).slice(0, 6)}`;
  usedIds.add(withHash);
  return withHash;
}

function extractCsvReferences(value) {
  const references = [];

  if (value === undefined || value === null) {
    return references;
  }

  const text = String(value);
  let match;

  CSV_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = CSV_REFERENCE_PATTERN.exec(text)) !== null) {
    const path = normalizeCsvPath(match[1]);

    if (!path) {
      continue;
    }

    references.push({
      source: `CSV:${match[1]}`,
      path,
    });
  }

  return references;
}

function createCsvContext(diagnostics = []) {
  return {
    byPath: new Map(),
    usedIds: new Set(),
    diagnostics,
  };
}

function addCsvDependency(csvContext, reference = {}, metadata = {}) {
  if (!csvContext || !reference.path) {
    return null;
  }

  const path = normalizeCsvPath(reference.path);
  const key = normalizeCsvPathKey(path);
  let dependency = csvContext.byPath.get(key);

  if (!dependency) {
    dependency = {
      id: createCsvDependencyId(path, csvContext.usedIds),
      path,
      source: reference.source || `CSV:${path}`,
      required: metadata.required !== false,
      status: 'unverified',
      reason: 'browser_cannot_access_local_or_network_path',
      usedBy: [],
      sourceLine: metadata.sourceLine,
    };
    csvContext.byPath.set(key, dependency);
    addDiagnostic(csvContext.diagnostics, {
      level: 'warning',
      code: 'CSV_DEPENDENCY_DETECTED',
      path,
      dependencyId: dependency.id,
      field: metadata.usedBy,
      sourceLine: metadata.sourceLine,
      message: 'Se detecto una dependencia CSV externa.',
    });
    addDiagnostic(csvContext.diagnostics, {
      level: 'warning',
      code: 'CSV_PATH_NOT_VERIFIABLE',
      path,
      dependencyId: dependency.id,
      reason: dependency.reason,
      message: 'La ruta CSV no puede verificarse desde el navegador/runtime local.',
    });
  } else if (metadata.usedBy && !dependency.usedBy.includes(metadata.usedBy)) {
    addDiagnostic(csvContext.diagnostics, {
      level: 'info',
      code: 'CSV_DUPLICATED_REFERENCE',
      path,
      dependencyId: dependency.id,
      field: metadata.usedBy,
      message: 'La dependencia CSV ya habia sido registrada; se agrega referencia usedBy.',
    });
  }

  if (metadata.usedBy && !dependency.usedBy.includes(metadata.usedBy)) {
    dependency.usedBy.push(metadata.usedBy);
  }

  if (!dependency.sourceLine && metadata.sourceLine) {
    dependency.sourceLine = metadata.sourceLine;
  }

  return dependency;
}

function getCsvDependencyList(csvContext) {
  return [...(csvContext?.byPath?.values() || [])].map((dependency) => compactObject({
    ...dependency,
    usedBy: [...new Set(dependency.usedBy || [])],
  }));
}

function findFirstCsvReference(...values) {
  for (const value of values) {
    const [reference] = extractCsvReferences(value);

    if (reference) {
      return reference;
    }
  }

  return null;
}

function normalizeDatasourceOption(option = {}) {
  return {
    text: option.text || option.label || option.value,
    value: option.value ?? '',
    actionType: option.actionType,
    target: option.target,
    actionRef: option.actionRef,
  };
}

function datasourceOptionSignature(option = {}) {
  return JSON.stringify({
    text: option.text || '',
    value: option.value ?? '',
    actionType: option.actionType || '',
    target: option.target || '',
    actionRef: option.actionRef || '',
  });
}

function uniqueDatasourceValue(option, usedValues) {
  const base = String(option.value || safeMenuValue(option.text) || 'OPTION');
  let candidate = base;
  let index = 2;

  while (usedValues.has(candidate)) {
    candidate = `${base}_${index}`;
    index += 1;
  }

  return candidate;
}

function dedupeDatasourceOptions(options = [], context = null, scopeId = '') {
  const usedValues = new Set();
  const valueSignatures = new Map();
  const normalizedOptions = [];

  options.forEach((rawOption) => {
    const option = normalizeDatasourceOption(rawOption);
    let value = String(option.value ?? '');
    const signature = datasourceOptionSignature(option);

    if (usedValues.has(value)) {
      const previousSignature = valueSignatures.get(value);

      if (previousSignature === signature) {
        context?.duplicates && (context.duplicates.optionsSkipped += 1);
        return;
      }

      value = uniqueDatasourceValue(option, usedValues);
      option.value = value;
    }

    usedValues.add(value);
    valueSignatures.set(value, datasourceOptionSignature(option));
    normalizedOptions.push(compactObject(option));
  });

  if (options.length > 0 && normalizedOptions.length === 0) {
    addDiagnostic(context?.diagnostics || [], {
      level: 'warning',
      code: 'DATASOURCE_DEDUPED_TO_EMPTY',
      scopeId,
      message: 'El datasource quedo vacio despues de deduplicar opciones.',
    });
  }

  return normalizedOptions;
}

function getSelectCatalogCandidates(field = {}, variableName = '') {
  return [
    field.name,
    field.binding,
    field.variable,
    field.variableBase,
    variableName,
    normalizeVariableName(field.name || ''),
    normalizeVariableName(field.binding || ''),
    normalizeVariableName(field.variable || ''),
    normalizeVariableName(field.variableBase || ''),
    normalizeVariableName(variableName || ''),
  ].filter(Boolean);
}

function findCatalogForSelect(ir, field = {}, variableName = '') {
  const catalogs = ir.catalogs || {};
  const catalogValues = Object.values(catalogs);

  if (field.catalog) {
    const directCatalog = catalogs[field.catalog] || catalogValues.find((catalog) => catalog.id === field.catalog);

    if (directCatalog) {
      return directCatalog;
    }
  }

  const candidateNames = new Set(getSelectCatalogCandidates(field, variableName).map(normalizeLookupName).filter(Boolean));

  return (
    catalogValues.find((catalog) => {
      const catalogNames = [
        catalog.variable,
        catalog.sourceVariable,
        catalog.target,
        catalog.name,
      ].map(normalizeLookupName);

      return catalogNames.some((catalogName) => candidateNames.has(catalogName));
    }) || null
  );
}

function getSelectDatasource(field, ir, variableName, context) {
  const catalog = findCatalogForSelect(ir, field, variableName);
  const catalogOptions = catalog?.options || [];
  const fieldOptions = getFieldOptions(field);
  const datasource = normalizeDatasource(
    catalogOptions.length > 0 ? catalogOptions : fieldOptions,
    context,
    field.name || field.binding || field.variable || variableName
  );

  if (datasource.length === 0) {
    context.diagnostics.push({
      level: 'warning',
      code: 'SELECT_WITHOUT_CATALOG',
      field: field.name || field.binding || field.variable || variableName,
      message: 'El campo select no tiene datasource porque no se encontro catalogo en IR.catalogs.',
    });
  }

  return {
    catalog,
    datasource,
  };
}

function isDisplayField(field = {}) {
  const explicitDisplay =
    field.type === 'display' ||
    field.controlType === 'label' ||
    field.control === 'display';

  if (explicitDisplay) {
    return true;
  }

  const isWindowInput =
    field.type === 'field' ||
    field.type === 'select' ||
    field.controlType === 'input' ||
    field.control === 'input' ||
    field.controlType === 'select' ||
    field.control === 'select' ||
    field.suffix === '#D' ||
    field.suffix === '#C';

  if (isWindowInput) {
    return false;
  }

  return (
    field.classification === 'read_display' ||
    field.variableClass === 'read_display'
  );
}

function getCommonPrefix(values = []) {
  if (values.length === 0) {
    return '';
  }

  return values.reduce((prefix, value) => {
    let index = 0;

    while (index < prefix.length && index < value.length && prefix[index] === value[index]) {
      index += 1;
    }

    return prefix.slice(0, index);
  });
}

function normalizeOptionValue(value = '') {
  return normalizeVariableName(value)
    .replace(/^@/, '')
    .replace(/[^A-Z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function deriveCheckboxGroupOptions(options = []) {
  const optionVariables = options.map((option) => normalizeOptionValue(option.variable || option.name || '')).filter(Boolean);
  const commonPrefix = getCommonPrefix(optionVariables);

  return options.map((option, index) => {
    const optionVariable = normalizeOptionValue(option.variable || option.name || '');
    const structuralValue = commonPrefix && optionVariable.startsWith(commonPrefix) ? optionVariable.slice(commonPrefix.length) : '';
    const fallbackValue = normalizeOptionValue(option.value || option.label || option.variable || String(index + 1));

    return {
      text: option.text || option.label || option.variable,
      value: structuralValue || fallbackValue,
      variable: option.variable || option.name,
      name: normalizeDomName(option.variable || option.name || ''),
      binding: normalizeVariableName(option.variable || option.name || ''),
    };
  });
}

function normalizeText(value = '') {
  return value.trim().replace(/\s+/g, ' ');
}

function slugify(value = '') {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function safeMenuValue(text = '') {
  return String(text || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function createMessageName(index = 0) {
  return `msg_${index + 1}`;
}

function isSeparator(label = '') {
  return /^[-_=]{3,}$/.test(label.trim());
}

function createUniqueName(baseName, usedNames) {
  const cleanBaseName = baseName || 'field';
  let candidate = cleanBaseName;
  let index = 2;

  while (usedNames.has(candidate)) {
    candidate = `${cleanBaseName}_${index}`;
    index += 1;
  }

  usedNames.add(candidate);
  return candidate;
}

function extractTemplateVariables(template = '') {
  const variables = [];
  let match;

  PLACEHOLDER_PATTERN.lastIndex = 0;

  while ((match = PLACEHOLDER_PATTERN.exec(template)) !== null) {
    const variableName = normalizeDomName(match[1]);

    if (!variableName || variables.includes(variableName)) {
      continue;
    }

    variables.push(variableName);
  }

  return variables;
}

function extractBracketVariables(expression = '') {
  const variables = [];
  let match;

  BRACKET_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = BRACKET_REFERENCE_PATTERN.exec(expression)) !== null) {
    const variableName = normalizeDomName(match[1]);

    if (!variableName || variables.includes(variableName)) {
      continue;
    }

    variables.push(variableName);
  }

  return variables;
}

function createLabelField(field, context) {
  const label = normalizeText(field.text || field.label || '');
  const separator = isSeparator(label);
  const baseName = separator ? `separator_${context.separatorIndex}` : createMessageName(context.messageIndex);

  if (separator) {
    context.separatorIndex += 1;
  } else {
    context.messageIndex += 1;
  }

  return {
    name: createUniqueName(baseName, context.usedNames),
    label,
    typedata: 'label',
    visible: true,
    readonly: true,
    displayOnly: true,
    value: '',
  };
}

function createDisplayField(field, ir, context) {
  const variableName = normalizeVariableName(field.binding || field.variable || field.name);
  const variable = findVariableMetadata(ir, variableName);
  const sourceName = normalizeDomName(variableName);
  const csvReference = getFieldCsvReference(field, ir, variableName);

  context.visibleBindings.add(variableName.toUpperCase());

  const normalizedField = compactObject(applyDefineMetadataToField({
    _sourceName: sourceName,
    name: createUniqueName(sourceName, context.usedNames),
    label: field.label || variableName,
    typedata: 'label',
    visible: true,
    readonly: true,
    displayOnly: true,
    binding: variableName,
    value: '',
    templateValue: field.templateValue || `{${variableName}}`,
    textTemplate: field.textTemplate || `${field.label || variableName}: {${variableName}}`,
    datatype: field.datatype || variable?.datatype,
    maxlength: resolveFieldMaxlength(field, variable),
    source: field.source || variable?.source,
    classification: field.classification || field.variableClass || variable?.classification,
  }, variable));

  return csvReference
    ? attachCsvCatalogSource(normalizedField, context.csvDependencies, csvReference, {
        usedBy: variableName,
        sourceLine: field.sourceLine || field.line || variable?.line,
      })
    : normalizedField;
}

function createButtonField(field, context) {
  const action = field.action || field.button || '';
  const buttonName = normalizeDomName(action || field.label || `button_${context.buttonIndex}`);

  context.buttonIndex += 1;

  return {
    _sourceName: buttonName,
    name: createUniqueName(buttonName, context.usedNames),
    label: field.label || buttonName,
    typedata: 'button',
    visible: true,
    action,
    value: '',
  };
}

function createEditableField(field, ir, context) {
  const variableName = normalizeVariableName(field.binding || field.variable || field.name);
  const variable = findVariableMetadata(ir, variableName);
  const typedata = normalizeTypedata(field, variable);
  const selectData = typedata === 'select' ? getSelectDatasource(field, ir, variableName, context) : null;
  const datasource = selectData?.datasource;
  const sourceName = normalizeDomName(variableName);
  const fieldSource = field.source || variable?.source;
  const required = resolveFieldRequired(field, variable);
  const rawClassification = field.classification || field.variableClass || variable?.classification;
  const csvReference = getFieldCsvReference(field, ir, variableName);
  const classification =
    fieldSource === 'read' || rawClassification === 'read_only' || rawClassification === 'read_display'
      ? 'read_prefilled'
      : 'user_input';

  context.visibleBindings.add(variableName.toUpperCase());

  const normalizedField = compactObject(applyDefineMetadataToField({
    _sourceName: sourceName,
    name: createUniqueName(sourceName, context.usedNames),
    label: field.label || variableName,
    typedata,
    visible: field.visible !== false,
    readonly: false,
    displayOnly: false,
    binding: variableName,
    value: field.value ?? getInitialValue(ir, variableName),
    required,
    mandatory: required,
    coincidence: DEFAULT_COINCIDENCE,
    maxlength: resolveFieldMaxlength(field, variable),
    datatype: field.datatype || variable?.datatype,
    source: fieldSource,
    classification,
    catalog: selectData?.catalog?.id || field.catalog,
    datasource,
    sourceLine: field.sourceLine || field.line,
    layoutGroup: field.layoutGroup,
  }, variable));

  if (typedata === 'select') {
    normalizedField.source_additional = field.source_additional || [];
  }

  return csvReference
    ? attachCsvCatalogSource(normalizedField, context.csvDependencies, csvReference, {
        usedBy: variableName,
        sourceLine: field.sourceLine || field.line || variable?.line,
      })
    : normalizedField;
}

function normalizeField(field, ir, context) {
  if (!field || field.visible === false) {
    return [];
  }

  if (field.type === 'button') {
    return [createButtonField(field, context)];
  }

  if (field.type === 'field_row') {
    const layoutGroup = `sst-line-${field.sourceLine || field.line || context.usedNames.size}`;
    return (field.parts || []).flatMap((part) => normalizeField({
      ...part,
      layoutGroup,
      sourceLine: part.sourceLine || part.line || field.sourceLine || field.line,
    }, ir, context));
  }

  if (field.type === 'checkbox-group') {
    const baseName = slugify(field.label || `checkbox_group_${context.checkboxGroupIndex}`);
    context.checkboxGroupIndex += 1;

    return [
      compactObject({
        name: createUniqueName(baseName, context.usedNames),
        label: field.label,
        typedata: 'check_group',
        visible: field.visible !== false,
        readonly: false,
        displayOnly: false,
        value: '',
        options: deriveCheckboxGroupOptions(field.options || []).map((option) => {
          context.visibleBindings.add(normalizeVariableName(option.variable).toUpperCase());
          return option;
        }),
      }),
    ];
  }

  if (hasWindowVariableBinding(field)) {
    return [
      createEditableField(
        {
          ...field,
          type: field.type === 'display' ? 'field' : field.type,
          controlType: field.controlType === 'label' ? undefined : field.controlType,
          control: field.control === 'display' ? undefined : field.control,
          variable: field.variable || field.binding || field.name,
          binding: field.binding || field.variable || field.name,
          classification:
            field.classification === 'read_display' || field.classification === 'display'
              ? undefined
              : field.classification,
          variableClass: field.variableClass === 'read_display' ? undefined : field.variableClass,
          readonly: false,
          displayOnly: false,
        },
        ir,
        context
      ),
    ];
  }

  if (field.type === 'text' || field.type === 'system') {
    return [createLabelField(field, context)];
  }

  if (isDisplayField(field)) {
    return [createDisplayField(field, ir, context)];
  }

  if (field.variable) {
    return [createEditableField(field, ir, context)];
  }

  return [];
}

function createConditionRef(action, ir) {
  const conditions = action.conditions || [];
  const buttonCondition = conditions.find((condition) => {
    return condition.left === '@BTN_PRSD' && condition.operator === '=' && condition.rightType === 'literal';
  });

  if (buttonCondition) {
    const matchingButton = Object.values(ir.buttonDefinitions || {}).find((buttonDefinition) => {
      return buttonDefinition.label === buttonCondition.right;
    });

    if (matchingButton) {
      return normalizeDomName(matchingButton.button);
    }
  }

  const conditionText = conditions
    .map((condition) => {
      if (condition.join) {
        return condition.join;
      }

      return `${condition.left}${condition.operator}${condition.right}`;
    })
    .join('');

  return conditionText ? `if:${conditionText}` : null;
}

function collectRemarkActions(actions = [], ir, conditionPath = []) {
  return actions.flatMap((action) => {
    if (!action) {
      return [];
    }

    if (action.type === 'remark') {
      return [
        {
          ...action,
          conditionRef: conditionPath[conditionPath.length - 1],
          conditionPath,
        },
      ];
    }

    if (action.type === 'ssr') {
      return [
        {
          type: 'ssr',
          template: action.template || action.expression,
          remarkType: 'SSR',
          skip: action.skip !== false,
          conditionRef: conditionPath[conditionPath.length - 1],
          conditionPath,
        },
      ];
    }

    if (action.type === 'if') {
      const conditionRef = createConditionRef(action, ir);
      const nextConditionPath = conditionRef ? [...conditionPath, conditionRef] : conditionPath;
      const thenRemarks = collectRemarkActions(action.then || [], ir, nextConditionPath);
      const elseRemarks = collectRemarkActions(action.else || [], ir, conditionRef ? [...conditionPath, `${conditionRef}:else`] : conditionPath);

      return [...thenRemarks, ...elseRemarks];
    }

    return [];
  });
}

function collectRemarks(ir) {
  const blockRemarks = Object.values(ir.blocks || {}).flatMap((block) => collectRemarkActions(block.actions || [], ir));

  if (blockRemarks.length > 0) {
    return blockRemarks;
  }

  return ir.remarks || [];
}

function getRemarkBlock(template = '') {
  const blockMatch = template.match(/^(\d+)/);

  return blockMatch ? Number(blockMatch[1]) : null;
}

function normalizeRemark(remark) {
  const template = String(remark.template || remark.remark || remark.text || '')
    .replace(/\s+\b(?:ENDIF|ELSE|THEN)\b.*$/i, '')
    .trim();
  const typeremark = remark.remarkType || remark.typeremark || 'Historical';

  return compactObject({
    block: typeremark === 'SSR' || remark.type === 'ssr' ? 3 : getRemarkBlock(template),
    typeremark,
    remark: template,
    remark_mask: template,
    skip: remark.skip !== false,
    processed: false,
    campoForm: extractTemplateVariables(template),
    coincidence: DEFAULT_COINCIDENCE,
    conditionRef: remark.conditionRef,
    conditionPath: remark.conditionPath && remark.conditionPath.length > 1 ? remark.conditionPath : undefined,
  });
}

function dedupeRemarks(remarks) {
  const seen = new Set();

  return remarks.filter((remark) => {
    const key = [remark.remark, remark.conditionRef || '', JSON.stringify(remark.conditionPath || [])].join('|');

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function addVariableReference(target, variableName) {
  const rawName = normalizeVariableName(variableName);
  const normalizedName = rawName.startsWith('@') ? rawName : normalizeDomName(rawName);

  if (!normalizedName) {
    return;
  }

  target.add(normalizedName.toUpperCase());
}

function collectConditionVariables(conditions = [], target) {
  conditions.forEach((condition) => {
    if (condition.join) {
      return;
    }

    addVariableReference(target, condition.left);

    if (condition.rightType === 'variable') {
      addVariableReference(target, condition.right);
    }
  });
}

function collectActionVariables(action, target) {
  if (!action) {
    return;
  }

  if (['command', 'remark', 'ssr'].includes(action.type)) {
    extractTemplateVariables(action.template || action.expression || '').forEach((variableName) => addVariableReference(target, variableName));
  }

  if (action.type === 'read') {
    (action.targets || [action.target]).filter(Boolean).forEach((variableName) => addVariableReference(target, variableName));
  }

  if (action.type === 'default') {
    addVariableReference(target, action.target);

    if (action.valueType === 'variable') {
      addVariableReference(target, action.value);
    }
  }

  if (action.type === 'if') {
    collectConditionVariables(action.conditions || [], target);
    (action.then || []).forEach((childAction) => collectActionVariables(childAction, target));
    (action.else || []).forEach((childAction) => collectActionVariables(childAction, target));
  }

  if (['compute', 'require', 'write'].includes(action.type)) {
    extractBracketVariables(action.expression || '').forEach((variableName) => addVariableReference(target, variableName));
  }
}

function collectLogicVariables(ir) {
  const variables = new Set();

  Object.values(ir.blocks || {}).forEach((block) => {
    (block.actions || []).forEach((action) => collectActionVariables(action, variables));
  });

  if (variables.size === 0) {
    (ir.logic || []).forEach((action) => collectActionVariables(action, variables));
  }

  return variables;
}

function createHiddenField(variableName, ir, context) {
  const normalizedName = normalizeVariableName(variableName);
  const domName = normalizeDomName(normalizedName);
  const variable = findVariableMetadata(ir, normalizedName);
  const value = getInitialValue(ir, normalizedName);
  const isSystem = String(variableName).startsWith('@');
  const source = isSystem ? 'system' : variable?.source === 'default' && variable?.line ? 'define' : variable?.source || 'define';
  const csvReference = getFieldCsvReference({ name: normalizedName, binding: normalizedName, value }, ir, normalizedName);

  const hiddenField = compactObject(applyDefineMetadataToField({
    _sourceName: domName,
    name: createUniqueName(domName, context.usedNames),
    label: domName,
    typedata: normalizeTypedata({}, variable),
    visible: false,
    readonly: true,
    value,
    maxlength: resolveFieldMaxlength({}, variable),
    datatype: variable?.datatype,
    required: variable?.required === true ? true : undefined,
    mandatory: variable?.required === true ? true : undefined,
    source,
    systemName: isSystem ? variableName : undefined,
  }, variable));

  return csvReference
    ? attachCsvCatalogSource(hiddenField, context.csvDependencies, csvReference, {
        usedBy: normalizedName,
        sourceLine: variable?.line,
      })
    : hiddenField;
}

function appendHiddenFields(fields, remarks, ir, context) {
  const referencedVariables = new Set([...remarks.flatMap((remark) => remark.campoForm || []), ...collectLogicVariables(ir)]);
  const hiddenFields = [];

  referencedVariables.forEach((variableName) => {
    const normalizedName = normalizeDomName(variableName).toUpperCase();

    if (!normalizedName || context.visibleBindings.has(normalizedName)) {
      return;
    }

    const variable = findVariable(ir, normalizedName);
    const isSystem = String(variableName).startsWith('@');

    if (!variable && !isSystem) {
      return;
    }

    hiddenFields.push(createHiddenField(isSystem ? variableName : normalizedName, ir, context));
    context.visibleBindings.add(normalizedName);
  });

  return [...fields, ...hiddenFields];
}

function createFormContext(diagnostics = [], duplicates = createDuplicateReport(), csvDependencies = createCsvContext(diagnostics)) {
  return {
    buttonIndex: 1,
    checkboxGroupIndex: 1,
    messageIndex: 0,
    separatorIndex: 1,
    usedNames: new Set(),
    visibleBindings: new Set(),
    diagnostics,
    duplicates,
    csvDependencies,
  };
}

function getMenuActionRef(item = {}) {
  const submenuAction = (item.actions || []).find((action) => action.type === 'submenu') || (item.action?.type === 'submenu' ? item.action : null);
  const navigationAction = (item.actions || []).find((action) => ['call', 'goto', 'exit'].includes(action.type)) || item.action;

  if (submenuAction) {
    return compactObject({
      type: 'submenu',
      target: submenuAction.targetId || submenuAction.target,
      title: submenuAction.title,
    });
  }

  if (navigationAction && ['call', 'goto', 'exit'].includes(navigationAction.type)) {
    return compactObject({
      type: navigationAction.type,
      target: navigationAction.target,
    });
  }

  return {
    type: 'menu_option',
  };
}

function getMenuFormId(menu = {}) {
  return safeMenuValue(menu.id || menu.key || menu.title || 'MENU') || 'MENU';
}

function getMenuItems(menu = {}) {
  return menu.items || menu.options || [];
}

function getMenuOptionActionType(item = {}) {
  if (['submenu', 'actions', 'call', 'goto', 'exit', 'showWindow'].includes(item.actionType)) {
    return item.actionType;
  }

  const actions = item.actions || [];
  const submenuAction = actions.find((action) => action.type === 'submenu') || (item.action?.type === 'submenu' ? item.action : null);
  const executableActions = actions.filter((action) => action.type !== 'submenu');

  if (submenuAction) {
    return 'submenu';
  }

  if (executableActions.length === 0) {
    return 'unknown';
  }

  if (executableActions.length === 1 && ['call', 'goto', 'exit', 'window', 'showWindow'].includes(executableActions[0].type)) {
    return executableActions[0].type === 'window' ? 'showWindow' : executableActions[0].type;
  }

  return 'actions';
}

function getMenuOptionTarget(item = {}) {
  const actions = item.actions || [];
  const submenuAction = actions.find((action) => action.type === 'submenu') || (item.action?.type === 'submenu' ? item.action : null);

  if (submenuAction) {
    return submenuAction.targetId || submenuAction.target || item.target || null;
  }

  const action = actions.find((candidate) => ['call', 'goto', 'window', 'showWindow'].includes(candidate.type)) || item.action;

  if (!action) {
    return item.target || null;
  }

  return action.target || action.targetId || action.formRef || action.id || item.target || null;
}

function getMenuOptionActionRef(menu, item) {
  const actionType = getMenuOptionActionType(item);

  if (actionType !== 'actions') {
    return item.actionRef;
  }

  return item.actionRef || `${getMenuFormId(menu)}_OPTION_${safeMenuValue(item.text || item.value)}`;
}

function normalizeMenuDatasourceItem(menu, item) {
  const actionType = getMenuOptionActionType(item);

  return compactObject({
    text: item.text,
    value: item.value || safeMenuValue(item.text),
    actionType,
    target: actionType === 'actions' ? null : getMenuOptionTarget(item),
    actionRef: getMenuOptionActionRef(menu, item),
  });
}

function collectMenus(menus = []) {
  const collected = [];
  const seenMenuIds = new Set();

  function visit(menu) {
    if (!menu) {
      return;
    }

    const menuId = getMenuFormId(menu);

    if (seenMenuIds.has(menuId)) {
      return;
    }

    seenMenuIds.add(menuId);
    collected.push(menu);
    (menu.items || []).forEach((item) => {
      if (item.submenu) {
        visit(item.submenu);
        return;
      }

      (item.actions || []).forEach((action) => {
        if (action.type === 'submenu' && action.menu) {
          visit(action.menu);
        }
      });
    });
  }

  menus.forEach(visit);

  return collected;
}

function getMenuRoots(ir = {}) {
  const menus = ir.menus || [];

  if ((ir.menuTree || []).length > 0) {
    return ir.menuTree;
  }

  const roots = menus.filter((menu) => !menu.parentKey && !menu.parent && !menu.parentId);

  return roots.length > 0 ? roots : menus;
}

function stripPrivateFieldMetadata(field = {}) {
  const { _sourceName, ...publicField } = field;

  return publicField;
}

function isVisibleField(field = {}) {
  return field.visible !== false && field.typedata !== 'hidden';
}

function isDataJsonCapturableField(field = {}) {
  if (!isVisibleField(field) || field.displayOnly === true || field.readonly === true) {
    return false;
  }

  return [
    'text',
    'numeric',
    'decimal',
    'mail',
    'date',
    'date_sabre',
    'date_dd_mm_yy',
    'hour_sabre',
    'check',
    'check_group',
    'select',
    'select_menu',
  ].includes(field.typedata);
}

function getDataJsonFormMetrics(fields = []) {
  return {
    fieldCount: fields.filter(isDataJsonCapturableField).length,
    messageCount: fields.filter((field) => isVisibleField(field) && field.typedata === 'label').length,
    buttonCount: fields.filter((field) => isVisibleField(field) && ['button', 'button_menu'].includes(field.typedata)).length,
  };
}

function resolveDataJsonWindowType(form = {}, metrics = getDataJsonFormMetrics(form.fields || [])) {
  if (form.windowType === 'menu') {
    return 'menu';
  }

  if (metrics.fieldCount > 0) {
    return 'form';
  }

  if (metrics.buttonCount > 0) {
    return 'confirmation';
  }

  return 'validation';
}

function dedupeFields(fields = [], formId = '', context = null) {
  const seenFieldNames = new Set();
  const dedupedFields = [];

  fields.forEach((field) => {
    const fieldName = field._sourceName || field.name;

    if (!fieldName) {
      dedupedFields.push(stripPrivateFieldMetadata(field));
      return;
    }

    if (seenFieldNames.has(fieldName)) {
      context?.duplicates && (context.duplicates.fieldsSkipped += 1);
      addDiagnostic(context?.diagnostics || [], {
        level: 'warning',
        code: 'DUPLICATE_FIELD_SKIPPED',
        formId,
        field: field.name,
        message: 'Se omitio un campo duplicado dentro del mismo form.',
      });
      return;
    }

    seenFieldNames.add(fieldName);
    dedupedFields.push(stripPrivateFieldMetadata(field));
  });

  return dedupedFields;
}

function dedupeForms(forms = [], context = null) {
  const seenFormKeys = new Set();
  const dedupedForms = [];

  forms.forEach((form) => {
    const formId = form.id || form.title;
    const sourceLine = form.sourceLine || form.line;
    const formKey = sourceLine ? `line:${sourceLine}` : `id:${formId}`;

    if (!formKey) {
      dedupedForms.push(form);
      return;
    }

    if (seenFormKeys.has(formKey)) {
      context?.duplicates && (context.duplicates.formsSkipped += 1);
      addDiagnostic(context?.diagnostics || [], {
        level: 'warning',
        code: 'DUPLICATE_FORM_SKIPPED',
        formId,
        sourceLine,
        message: 'Se omitio un form duplicado en data.json.',
      });
      return;
    }

    seenFormKeys.add(formKey);
    dedupedForms.push(form);
  });

  return dedupedForms;
}

function normalizeMenuForm(menu, context = { diagnostics: [], duplicates: createDuplicateReport() }) {
  const menuId = getMenuFormId(menu);
  const selectName = `${menuId}_SELECT`;
  const items = getMenuItems(menu).filter((item) => item?.visible !== false);
  const datasource = dedupeDatasourceOptions(
    items.map((item) => normalizeMenuDatasourceItem(menu, item)),
    context,
    menuId
  );

  if (datasource.length === 0) {
    context.diagnostics.push({
      level: 'warning',
      code: 'MENU_WITHOUT_OPTIONS',
      menuId,
      message: 'Se detecto MENU, pero no se encontraron opciones.',
    });
  }

  datasource.forEach((option) => {
    if (option.actionType === 'unknown') {
      context.diagnostics.push({
        level: 'warning',
        code: 'MENU_OPTION_WITHOUT_ACTION',
        menuId,
        option: option.text,
        message: 'La opcion de MENU no tiene una accion clara.',
      });
    }
  });

  return compactObject({
    id: menuId,
    sourceId: menu.id,
    title: menu.title || menu.id,
    title_visible: true,
    windowType: 'menu',
    visible: menu.visible !== false,
    parent: menu.parentId || menu.parent || undefined,
    fields: [
      compactObject({
        name: selectName,
        label: menu.title || menu.id || 'MENU',
        typedata: 'select_menu',
        visible: true,
        mandatory: true,
        value: '',
        datasource,
      }),
      {
        name: `${menuId}_CONTINUE`,
        label: 'Continuar',
        typedata: 'button_menu',
        visible: true,
        action: 'continue_menu',
        targetField: selectName,
        actionRef: {
          type: 'menu_continue',
          targetField: selectName,
          menuId,
        },
        value: '',
      },
    ],
  });
}

function normalizeForm(form, ir, remarks, diagnostics, duplicates, csvDependencies) {
  const context = createFormContext(diagnostics, duplicates, csvDependencies);
  const fields = (form.fields || []).flatMap((field) => normalizeField(field, ir, context));
  const fieldsWithHidden = appendHiddenFields(fields, remarks, ir, context);
  const dedupedFields = dedupeFields(fieldsWithHidden, form.id, context);
  const metrics = getDataJsonFormMetrics(dedupedFields);

  return compactObject({
    id: form.id,
    legacyId: form.legacyId,
    title: form.title,
    title_visible: true,
    windowType: resolveDataJsonWindowType(form, metrics),
    visible: form.visible !== false,
    sourceLine: form.sourceLine || form.line,
    row: form.row,
    column: form.column,
    raw: form.raw,
    fieldCount: metrics.fieldCount,
    messageCount: metrics.messageCount,
    buttonCount: metrics.buttonCount,
    fields: dedupedFields,
  });
}

function getCsvUsedByFromObject(value = {}) {
  return normalizeVariableName(
    value.target ||
      value.name ||
      value.binding ||
      value.variable ||
      value.variableBase ||
      value.sourceVariable ||
      ''
  );
}

function getCsvSourceLineFromObject(value = {}) {
  return value.sourceLine || value.line || value.lineNumber;
}

function collectIrCsvDependencies(ir = {}, csvDependencies) {
  const seen = new WeakSet();

  function visit(value, parent = null) {
    if (value === null || value === undefined) {
      return;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      extractCsvReferences(value).forEach((reference) => {
        addCsvDependency(csvDependencies, reference, {
          usedBy: parent && typeof parent === 'object' ? getCsvUsedByFromObject(parent) : undefined,
          sourceLine: parent && typeof parent === 'object' ? getCsvSourceLineFromObject(parent) : undefined,
        });
      });
      return;
    }

    if (typeof value !== 'object') {
      return;
    }

    if (seen.has(value)) {
      return;
    }

    seen.add(value);

    if (Array.isArray(value)) {
      value.forEach((entry) => visit(entry, parent));
      return;
    }

    Object.values(value).forEach((entry) => visit(entry, value));
  }

  visit(ir);
}

export function generateDataJson(ir = {}) {
  const diagnostics = [];
  const duplicates = createDuplicateReport();
  const csvDependencies = createCsvContext(diagnostics);
  const diagnosticsContext = { diagnostics, duplicates };
  const remarks = dedupeRemarks(collectRemarks(ir).map(normalizeRemark));
  const menuForms = collectMenus(getMenuRoots(ir)).map((menu) => normalizeMenuForm(menu, diagnosticsContext));
  const forms = (ir.forms || []).map((form) => normalizeForm(form, ir, remarks, diagnostics, duplicates, csvDependencies));
  const allForms = dedupeForms([...forms, ...menuForms], diagnosticsContext);
  collectIrCsvDependencies(ir, csvDependencies);
  const csvDependencyList = getCsvDependencyList(csvDependencies);
  const dataJson = compactObject({
    title: ir.title,
    editCatalogues: false,
    reintentos: 0,
    sendCommands: false,
    validateActiveReservation: false,
    dependencies: csvDependencyList.length > 0 ? { csv: csvDependencyList } : undefined,
    forms: allForms,
    outputs: remarks.length > 0 ? { remarks } : undefined,
    diagnostics: {
      warnings: diagnostics,
      duplicates,
    },
  });
  applyDefineMetadataToAllFields(dataJson, ir, diagnostics);
  const serializedLineCount = JSON.stringify(dataJson, null, 2).split('\n').length;

  if (allForms.length > 300) {
    diagnostics.push({
      level: 'warning',
      code: 'DATA_JSON_TOO_MANY_FORMS',
      message: 'data.json contiene mas de 300 forms; revisar duplicados o menus expansivos.',
      forms: allForms.length,
    });
  }

  if (serializedLineCount > 20000) {
    diagnostics.push({
      level: 'warning',
      code: 'DATA_JSON_TOO_LARGE',
      message: 'data.json parece contener duplicados o logica embebida.',
      lines: serializedLineCount,
    });
  }

  return dataJson;
}
